import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS, IMAGES } from "../../content.js";
import { EASE } from "../motion/Reveal.jsx";

function Wordmark({ dark = false }) {
  return (
    <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-3">
      <img
        src={dark ? "/oneiros/logo-white.webp" : "/oneiros/logo-black.png"}
        alt="Oneiros The Sports Club — Redefine Your Routine"
        className="h-[45px] w-auto"
      />
    </Link>
  );
}

const DARK_HERO_ROUTES = ["/", "/blue-membership"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const overDark = DARK_HERO_ROUTES.includes(location.pathname) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          overDark ? "bg-transparent" : "border-b border-hairline bg-white/95 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
          <Wordmark dark={overDark} />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                to={link.to}
                className={({ isActive }) =>
                  `relative pb-1.5 text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    isActive
                      ? overDark
                        ? "text-white"
                        : "text-navy"
                      : overDark
                        ? "text-white/70 hover:text-white"
                        : "text-mute hover:text-navy"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute inset-x-0 -bottom-0.5 h-[2px] bg-brand"
                        transition={{ duration: 0.35, ease: EASE }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              data-testid="navbar-enquire-btn"
              to="/contact"
              className="hidden rounded-full bg-brand px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-dark sm:inline-flex"
            >
              Enquire Now
            </Link>
            <button
              data-testid="navbar-mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors lg:hidden ${
                overDark ? "border-white/30 text-white" : "border-hairline text-navy"
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-drawer"
            className="grain fixed inset-0 z-[60] flex flex-col bg-navy px-6 py-5 text-white"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <div className="flex items-center justify-between">
              <Wordmark dark />
              <button
                data-testid="navbar-mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    to={link.to}
                    className={({ isActive }) =>
                      `group flex items-center justify-between border-b border-white/10 py-4 ${
                        isActive ? "text-brand" : "text-white"
                      }`
                    }
                  >
                    <span className="font-display text-3xl">{link.label}</span>
                    <ArrowUpRight className="h-5 w-5 text-white/40 transition-colors group-hover:text-brand" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
              className="mt-auto flex flex-col gap-5 pb-6"
            >
              <img src={IMAGES.logoWhite} alt="Oneiros The Sports Club" className="h-10 w-auto self-start object-contain" />
              <Link
                data-testid="mobile-enquire-btn"
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white"
              >
                Enquire Now
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
